import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Bike, KeyRound, Trash2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { ActionPerms } from "../ModulePermsEditor";

const Box = ({ icon: Icon, title, hint, children, testid }) => (
  <div className="rounded-xl border border-slate-200 p-3" data-testid={testid}>
    <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800"><Icon className="h-4 w-4 text-blue-600" /> {title}</p>
    {hint && <p className="mb-2 text-[11px] text-slate-500">{hint}</p>}
    {children}
  </div>
);

const Checks = ({ all, value, onSave, testid }) => {
  const [sel, setSel] = useState(value || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSel(value || []), [value]);
  const toggle = async (x) => {
    const next = sel.includes(x) ? sel.filter((y) => y !== x) : [...sel, x];
    setSel(next); setBusy(true);
    try { await onSave(next); toast.success("تم الحفظ"); } catch (e) { setSel(sel); toast.error(e?.response?.data?.detail || e?.message || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  if (!all.length) return <p className="text-xs text-slate-400">لا توجد عناصر</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {all.map((x) => (
        <button key={x} type="button" disabled={busy} onClick={() => toggle(x)} data-testid={`${testid}-${x}`}
          className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${sel.includes(x) ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}>{x}</button>))}
    </div>
  );
};

export const UserExtras = ({ u, reload, onClose, needBranch }) => {
  const [branches, setBranches] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.branchNames().then((b) => setBranches((Array.isArray(b) ? b : b?.items || []).filter(Boolean))).catch(() => {});
    api.ridersConfig().then((c) => setPlatforms(c?.platforms || [])).catch(() => {});
  }, []);
  const resetPw = async () => {
    if (!/^\d{6}$/.test(pw)) { toast.warning("الرمز السري يجب أن يكون 6 أرقام بالضبط"); return; }
    setBusy("pw");
    try { await api.setUserPassword(u.user_id, pw); setPw(""); toast.success("تم تعيين كلمة المرور الجديدة"); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التغيير"); } finally { setBusy(""); }
  };
  const remove = async () => {
    if (!window.confirm(`حذف المستخدم «${u.name}» من الشركة نهائياً؟`)) return;
    setBusy("del");
    try { await api.removeUser(u.email); toast.success("تم حذف المستخدم"); onClose(); reload(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); } finally { setBusy(""); }
  };
  const platNames = platforms.map((p) => (typeof p === "string" ? p : p.name)).filter(Boolean);
  return (
    <div className="mt-6 space-y-3" data-testid="user-extras">
      <Box icon={Building2} title="الفروع المسموحة" hint={needBranch ? "اختر فرعاً واحداً على الأقل — بدون فرع ما يشوف المستخدم أي بيانات" : "حسب نطاق دوره"} testid="user-branches">
        <Checks all={branches} value={u.allowed_branches} onSave={(v) => { if (needBranch && !v.length) return Promise.reject(new Error("لازم يبقى فرع واحد على الأقل")); return api.setUserBranchesByEmail(u.email, v).then(reload); }} testid="user-branch" />
      </Box>
      {platNames.length > 0 && (
        <Box icon={Bike} title="المنصات المسموحة" hint="بدون تحديد = كل المنصات" testid="user-platforms">
          <Checks all={platNames} value={u.allowed_platforms} onSave={(v) => api.setUserPlatforms(u.email, v).then(reload)} testid="user-platform" />
        </Box>
      )}
      <ActionPerms user={u} onSaved={reload} />
      {u.user_id && (
        <Box icon={KeyRound} title="إعادة تعيين كلمة المرور" testid="user-reset-pw">
          <div className="flex gap-2">
            <Input pin value={pw} onChange={(e) => setPw(e.target.value)} placeholder="كلمة المرور الجديدة" data-testid="user-reset-pw-input" />
            <Button disabled={busy === "pw"} onClick={resetPw} data-testid="user-reset-pw-btn">{busy === "pw" && <Loader2 className="h-4 w-4 animate-spin" />} تعيين</Button>
          </div>
        </Box>
      )}
      <Button variant="outline" disabled={busy === "del"} onClick={remove} className="w-full gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="user-delete-btn">
        <Trash2 className="h-4 w-4" /> حذف المستخدم من الشركة
      </Button>
    </div>
  );
};
